/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import { workspace, Disposable, EventEmitter, Memento, window, MessageItem, ConfigurationTarget, Uri, ConfigurationChangeEvent, l10n, env } from 'vscode';
import { $Smc } from './repository';
import { $1lc, $Vlc, $Ylc } from './util';
import { GitErrorCodes } from './api/git';

export class $fmc {

	private static a = 'autofetch.didInformUser';

	private b = new EventEmitter<boolean>();
	private f = this.b.event;

	private g: boolean = false;
	private h: boolean = false;
	get enabled(): boolean { return this.g; }
	set enabled(enabled: boolean) { this.g = enabled; this.b.fire(enabled); }

	private i: Disposable[] = [];

	constructor(private j: $Smc, private k: Memento) {
		workspace.onDidChangeConfiguration(this.m, this, this.i);
		this.m();

		const onGoodRemoteOperation = $Vlc(j.onDidRunOperation, ({ operation, error }) => !error && operation.remote);
		const onFirstGoodRemoteOperation = $Ylc(onGoodRemoteOperation);
		onFirstGoodRemoteOperation(this.l, this, this.i);
	}

	private async l(): Promise<void> {
		const didInformUser = !this.k.get<boolean>($fmc.a);

		if (this.enabled && !didInformUser) {
			this.k.update($fmc.a, true);
		}

		const shouldInformUser = !this.enabled && didInformUser;

		if (!shouldInformUser) {
			return;
		}

		const yes: MessageItem = { title: l10n.t('Yes') };
		const no: MessageItem = { isCloseAffordance: true, title: l10n.t('No') };
		const askLater: MessageItem = { title: l10n.t('Ask Me Later') };
		const result = await window.showInformationMessage(l10n.t('Would you like {0} to [periodically run "git fetch"]({1})?', env.appName, 'https://go.microsoft.com/fwlink/?linkid=865294'), yes, no, askLater);

		if (result === askLater) {
			return;
		}

		if (result === yes) {
			const gitConfig = workspace.getConfiguration('git', Uri.file(this.j.root));
			gitConfig.update('autofetch', true, ConfigurationTarget.Global);
		}

		this.k.update($fmc.a, true);
	}

	private m(e?: ConfigurationChangeEvent): void {
		if (e !== undefined && !e.affectsConfiguration('git.autofetch')) {
			return;
		}

		const gitConfig = workspace.getConfiguration('git', Uri.file(this.j.root));
		switch (gitConfig.get<boolean | 'all'>('autofetch')) {
			case true:
				this.h = false;
				this.enable();
				break;
			case 'all':
				this.h = true;
				this.enable();
				break;
			case false:
			default:
				this.h = false;
				this.disable();
				break;
		}
	}

	enable(): void {
		if (this.enabled) {
			return;
		}

		this.enabled = true;
		this.n();
	}

	disable(): void {
		this.enabled = false;
	}

	private async n(): Promise<void> {
		while (this.enabled) {
			await this.j.whenIdleAndFocused();

			if (!this.enabled) {
				return;
			}

			try {
				if (this.h) {
					await this.j.fetchAll({ silent: true });
				} else {
					await this.j.fetchDefault({ silent: true });
				}
			} catch (err) {
				if (err.gitErrorCode === GitErrorCodes.AuthenticationFailed) {
					this.disable();
				}
			}

			if (!this.enabled) {
				return;
			}

			const period = workspace.getConfiguration('git', Uri.file(this.j.root)).get<number>('autofetchPeriod', 180) * 1000;
			const timeout = new Promise(c => setTimeout(c, period));
			const whenDisabled = $1lc($Vlc(this.f, enabled => !enabled));

			await Promise.race([timeout, whenDisabled]);
		}
	}

	dispose(): void {
		this.disable();
		this.i.forEach(d => d.dispose());
	}
}
